import {
  lazy,
  Suspense,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  ViewTransition,
} from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { isNotesChannel, type ChannelWithMembership, type PublicUser } from '@avslack/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { auditMeKey, getAuditMe } from '../data/audit-search';
import {
  channelsKey,
  channelTitle,
  isListedChannel,
  sortChannels,
  useChannels,
  useReorderChannels,
} from '../data/channels';
import { useUsers } from '../data/users';
import { useDrafts, useScheduled } from '../data/workspace';
import { useReminderHistory } from '../data/reminders';
import { usePendingInvites } from '../data/calendar';
import { useRealtime } from '../realtime/RealtimeProvider';
import { useTheme } from '../lib/theme';
import { logout, openNotes } from '../lib/api';
import { belongsToTeam, useActiveTeam, useTeams } from '../data/teams';
import { clearDesktopSession } from '../lib/desktop';
import { NewChannelDialog } from './NewChannelDialog';
import { ChannelBrowser } from './ChannelBrowser';
import { ProfileDialog } from './ProfileDialog';
import { ReminderDialog } from './ReminderDialog';
import { ScheduledDialog } from './ScheduledDialog';
import { SettingsDialog } from './SettingsDialog';
import { UserStatus } from './UserStatus';
import { PresenceDot } from './PresenceDot';
import { Avatar } from './Avatar';
import { HuddleDot } from './HuddleControls';
import { Icon, type IconName } from './Icon';
import { phoneLineTitle, useMyPhoneLines } from '../data/phone';
import { Logo } from './Logo';
import { forgetOfflineCache } from '../lib/offline-cache';
import { clearOutbox } from '../data/outbox';
import { useSeenVersion } from '../changelog/seen';

// Az Ujdonsagok listaja (a teljes changelog) csak megnyitaskor toltodik le.
const ReleaseNotesDialog = lazy(() =>
  import('./ReleaseNotesDialog').then((module) => ({ default: module.ReleaseNotesDialog })),
);

/**
 * Az osszecsukott csoportokat is megjegyezzuk - gepenkent, a bongeszoben.
 *
 * SAJAT kulcs. Korabban ugyanaz volt (`avslack.sidebar.collapsed`), mint az
 * EGESZ oldalsav becsukasae (app-layout): az betolteskor `0`/`1`-et irt bele,
 * es ezzel minden frissiteskor felulirta a csoportok allapotat - mindegyik
 * lenyitva jott vissza.
 */
const COLLAPSE_KEY = 'avslack.sidebar.sections';

/** A harom rendezheto lista. A huzas mindig csoporton BELUL mozgat. */
type ListKey = 'favorites' | 'rooms' | 'directs';

function readCollapsed(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    // Csak objektumot fogadunk el - egy elrontott ertek ne torje el az oldalsavot.
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

interface SidebarProps {
  me: PublicUser;
  onOpenSwitcher: () => void;
  onCollapse: () => void;
}

export function Sidebar({ me, onOpenSwitcher, onCollapse }: SidebarProps) {
  const { data: channels } = useChannels();
  const queryClient = useQueryClient();
  // Melyik beszelgetes van nyitva - a kiemelt sor ebbol tudja, aktiv-e.
  const nyitottId = (useParams({ strict: false }) as { channelId?: string }).channelId;
  const { usersById, nameDisplay, presence, nameOf } = useUsers();
  const { status } = useRealtime();
  const { preference, cycle } = useTheme();
  /*
    Az audit-belepo csak jogosultnak latszik. A jovahagyasra varo keresek
    jelvenye miatt a JOGOSULTNAL percenkent frissul - mindenki masnal nem
    kerdezunk feleslegesen.
  */
  const { data: auditMe } = useQuery({
    queryKey: auditMeKey,
    queryFn: getAuditMe,
    staleTime: 60_000,
    refetchInterval: (query) => (query.state.data?.hasGrant ? 60_000 : false),
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  /** Melyik fulon nyiljon az emlekezteto-ablak: uj beallitas vagy elozmeny. */
  const [reminderTab, setReminderTab] = useState<'aktiv' | 'korabbiak'>('aktiv');
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  /* A megnyitaskori "lattam" allapot: a parbeszedben az ennel ujabbak kapnak "Uj" jelet. */
  const [seenVersion, markVersionSeen] = useSeenVersion();
  const [seenAtOpen, setSeenAtOpen] = useState<string | null>(null);
  const hasNews = seenVersion !== __APP_VERSION__;
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed);
  const navRef = useRef<HTMLElement>(null);
  const { byChannel: draftsByChannel } = useDrafts();
  const { data: scheduled } = useScheduled();
  /* Hany emlekezteto var meg kipipalasra - a belepo jelvenye ebbol all. */
  const nyitottEmlekeztetok = useReminderHistory().data?.unacked ?? 0;
  const reorder = useReorderChannels();

  /*
    KEDVENCEK.

    A csillagozott csatorna es a csillagozott ember EGY csoportba kerul,
    legfelul - mert aki csillagoz, az ezt keresi elsonek. Cserebe a sajat
    szekciojabol kikerul: ugyanaz a sor ket helyen csak zavarna.
  */
  const { activeTeamId, setActiveTeam } = useActiveTeam();
  const { data: teams } = useTeams();
  const { data: myPhoneLines } = useMyPhoneLines();

  /*
    A TELEFON kulon sorban all, a profil folott - nem a csatornak kozott. A
    vonal csatornaja nem beszelgetes, hanem a hivasok helye: aki hivast var,
    annak mindig ugyanott kell lennie, nem a csatornalista kozepen.
  */
  /*
    CSAK a vonal SAJAT csatornaja kerul ide. Ha a vonal egy altalanos csatornara
    csenget (#support), az a helyen marad a csatornak kozott - ott mas
    beszelgetes is folyik; a tarcsazot ott a csatorna fejlece adja.
  */
  const ownPhoneLines = useMemo(
    () => (myPhoneLines ?? []).filter((line) => line.place === 'own'),
    [myPhoneLines],
  );
  const phoneChannelIds = useMemo(
    () => new Set(ownPhoneLines.map((line) => line.channelId)),
    [ownPhoneLines],
  );
  const phoneRows = useMemo(
    () =>
      ownPhoneLines.flatMap((line) => {
        const channel = (channels ?? []).find((c) => c.id === line.channelId);
        return channel && belongsToTeam(channel, activeTeamId) && isListedChannel(channel)
          ? [{ line, channel }]
          : [];
      }),
    [ownPhoneLines, channels, activeTeamId],
  );

  const { favorites, rooms, directs, notes } = useMemo(() => {
    /*
      CSAK AZ AKTIV CEG.

      Egy masik ceg csatornaja nem "meg egy csatorna": mas emberek, mas munka,
      mas bizalmi kor. Egy listaban ulve minden nap el kellene olvasni a nevet
      ahhoz, hogy tudjuk, kinek irunk - es egyszer valaki nem olvasna el.

      Az ertesites ettol fuggetlenul cegeken at is megerkezik: a valto azt
      donti el, mit LATUNK, nem azt, mirol tudunk.
    */
    /*
      Az ARCHIVALT es az ELREJTETT csatorna nem kerul a listaba - mindketto a
      Beallitasok > Csatornak fulon erheto el (es a gyorskeresobol).
    */
    const sorted = sortChannels(channels ?? []).filter(
      (c) => belongsToTeam(c, activeTeamId) && !phoneChannelIds.has(c.id) && isListedChannel(c),
    );
    /*
      A SAJAT JEGYZETEK kulon sorban all, nem a DM-ek kozott.

      Formailag egytagu DM, de nem beszelgetes: nem valakivel, hanem magammal.
      A DM-lista elejen ulve ugy nezne ki, mint egy partner, akinek sosem
      valaszol - fent, a Mentett uzenetek mellett viszont az, ami: a sajat
      helyem.
    */
    const notes = sorted.find((c) => isNotesChannel(c, me.id)) ?? null;
    const rest = sorted.filter((c) => !c.membership.isStarred && c.id !== notes?.id);
    return {
      favorites: sorted.filter((c) => c.membership.isStarred && c.id !== notes?.id),
      rooms: rest.filter((c) => c.kind === 'public' || c.kind === 'private'),
      directs: rest.filter((c) => c.kind === 'dm' || c.kind === 'group_dm'),
      notes,
    };
  }, [channels, me.id, activeTeamId, phoneChannelIds]);

  /*
    A beszelgetes IGENY SZERINT keletkezik.

    Aki sosem jegyzetel, annak ne alljon ott egy ures beszelgetes; aki viszont
    rakattint, annak azonnal nyiljon meg. A hivas idempotens, tehat a masodik
    kattintas is ugyanoda visz.
  */
  const navigate = useNavigate();
  const [notesLoading, setNotesLoading] = useState(false);

  /**
   * Cegvaltas - a nyitott csatornaval egyutt.
   *
   * Enelkul a valto atbillent, de a jobb oldalon TOVABBRA IS a masik ceg
   * csatornaja allt: a felulet azt allitotta, hogy "Doki4Vet", kozben egy
   * ALPHAVET beszelgetes latszott. Epp az a keveredes, amit a valto megszuntet.
   */
  const valtas = (teamId: string) => {
    setActiveTeam(teamId);

    const elso = sortChannels(channels ?? []).find(
      (c) => belongsToTeam(c, teamId) && isListedChannel(c),
    );
    if (elso) void navigate({ to: '/c/$channelId', params: { channelId: elso.id } });
    else void navigate({ to: '/' });
  };

  /*
    A NYITOTT CSATORNA huzza magaval a cegvaltot.

    Egy linkre erkezve (ertesites, permalink, konyvjelzo) az is elofordulhat,
    hogy a megnyitott csatorna egy MASIK cege, mint ami a valton all. Ilyenkor
    a felulet ket kulonbozo dolgot allitana egyszerre.

    A cim a pontosabb szandek - valaki EZT a beszelgetest akarta megnyitni -,
    ezert a valto koveti, nem forditva.
  */
  useEffect(() => {
    if (!nyitottId) return;
    const nyitott = (channels ?? []).find((c) => c.id === nyitottId);
    if (!nyitott || belongsToTeam(nyitott, activeTeamId)) return;

    const cel =
      nyitott.kind === 'dm' || nyitott.kind === 'group_dm'
        ? (nyitott.dmTeamIds ?? [])[0]
        : nyitott.teamId;
    if (cel) setActiveTeam(cel);
  }, [nyitottId, channels, activeTeamId, setActiveTeam]);

  const openNotesChannel = async () => {
    if (notes) {
      void navigate({ to: '/c/$channelId', params: { channelId: notes.id } });
      return;
    }
    if (notesLoading) return;

    setNotesLoading(true);
    try {
      const channel = await openNotes();
      await queryClient.invalidateQueries({ queryKey: channelsKey });
      void navigate({ to: '/c/$channelId', params: { channelId: channel.id } });
    } finally {
      setNotesLoading(false);
    }
  };

  const toggleSection = (key: string) =>
    setCollapsed((current) => {
      const next = { ...current, [key]: !current[key] };
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      } catch {
        // Zart tarhely (privat ablak): a nyitas-csukas ilyenkor csak eddig el.
      }
      return next;
    });

  /**
   * Huzas utan: a TELJES sorrendet kuldjuk, nem csak az erintett csoportot.
   *
   * A szerver egyetlen szammal tarolja a sorrendet, es a harom lista ugyanabbol
   * a keszletbol epul. Ha csak az egyik csoportot szamoznank ujra, a masik ketto
   * szamai ossze-vissza csusznanak melle.
   */
  const commitOrder = (list: ListKey, ids: string[]) => {
    const groups: Record<ListKey, string[]> = {
      favorites: favorites.map((c) => c.id),
      rooms: rooms.map((c) => c.id),
      directs: directs.map((c) => c.id),
    };
    groups[list] = ids;
    reorder.mutate([...groups.favorites, ...groups.rooms, ...groups.directs]);
  };

  /** Egy sor - csatornahoz es emberhez is ugyanaz, csak mas kiseroadattal. */
  const renderRow = (channel: ChannelWithMembership) => {
    const isDirect = channel.kind === 'dm' || channel.kind === 'group_dm';
    const other = isDirect ? (channel.memberIds ?? []).find((id) => id !== me.id) : undefined;
    return (
      <ChannelRow
        channel={channel}
        label={
          isDirect ? channelTitle(channel, usersById, me.id, nameDisplay) : (channel.name ?? '')
        }
        userId={other}
        avatarUrl={other ? usersById.get(other)?.avatarUrl : undefined}
        presence={other ? presence[other] : undefined}
        hasDraft={draftsByChannel.has(channel.id)}
      />
    );
  };

  return (
    <aside className="brand-sidebar flex h-full flex-col border-r border-border-soft bg-sidebar">
      {/*
        A FEJLEC egyetlen sor.

        Korabban harom volt: a nev, alatta a "Keresés" gomb, alatta az
        "Ugrás…". Mindketto egy-egy teljes sort foglalt egy feliratert, amit
        ugyis mindenki fejbol tud - a hely pedig a csatornalistanak kellene.
        Most mindketto ikon, a jel mellett.
      */}
      <header className="flex items-center gap-2 px-3 pb-2 pt-3">
        <Logo className="h-8 w-8 shrink-0 text-accent" />
        <ConnectionPill status={status} />

        <div className="ml-auto flex items-center gap-0.5">
          <Link
            to="/search"
            search={{}}
            title="Keresés (Ctrl+Shift+F)"
            aria-label="Keresés"
            className="grid size-7 place-items-center rounded-control text-ink-faint transition-colors duration-(--duration-quick) hover:bg-sidebar-hover hover:text-ink [&.active]:bg-sidebar-active [&.active]:text-accent"
            activeProps={{ className: 'active' }}
          >
            <Icon name="keres" />
          </Link>

          <RailButton
            label="Le a lista aljára (mentett, naptár, jegyzetek…)"
            onClick={() =>
              navRef.current?.scrollTo({ top: navRef.current.scrollHeight, behavior: 'smooth' })
            }
          >
            <Icon name="nyilLe" />
          </RailButton>

          <RailButton label="Ugrás… (Ctrl+K)" onClick={onOpenSwitcher}>
            <Icon name="megnyitas" />
          </RailButton>

          {/*
            Az osszecsukas csak ASZTALON van ertelme.

            Mobilon az oldalsav maga a kezdolap - osszecsukva ures kepernyo
            maradna, ahonnan nincs tovabb.
          */}
          <span className="hidden md:contents">
            <RailButton label="Oldalsáv összecsukása (Ctrl+\)" onClick={onCollapse}>
              <Icon name="balra" />
            </RailButton>
          </span>
        </div>
      </header>

      {/*
        CEGVALTO - csak ha van mibol valtani.

        Egyetlen cegnel egy ilyen sav semmit nem mondana, viszont minden nap
        ott lenne. Ket cegtol felfele viszont ez a legfontosabb kapaszkodo:
        ebbol derul ki, KINEK irunk eppen.

        Az olvasatlan-jelzes a masik cegen is latszik - kulonben eszre sem
        vennenk, hogy ott tortenik valami, es a "cegek at is szol az ertesites"
        igerete ures maradna.
      */}
      {(teams?.length ?? 0) > 1 && (
        <div className="flex gap-1 border-b border-border-soft px-2 py-2">
          {(teams ?? []).map((team) => {
            const aktiv = team.id === activeTeamId;
            const olvasatlan = (channels ?? [])
              .filter(
                (c) => !c.membership.isMuted && isListedChannel(c) && belongsToTeam(c, team.id),
              )
              .reduce((osszeg, c) => osszeg + c.unreadCount, 0);

            return (
              <button
                key={team.id}
                type="button"
                onClick={() => valtas(team.id)}
                title={aktiv ? `${team.name} — ez van nyitva` : `Váltás ide: ${team.name}`}
                aria-pressed={aktiv}
                className={[
                  'flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-control',
                  'px-2 py-1.5 text-xs transition-colors duration-(--duration-quick)',
                  aktiv
                    ? 'bg-sidebar-active font-semibold shadow-(--shadow-soft)'
                    : 'text-ink-muted hover:bg-sidebar-hover hover:text-ink',
                ].join(' ')}
              >
                <span className="truncate">{team.name}</span>
                {!aktiv && olvasatlan > 0 && (
                  <span className="min-w-4 shrink-0 rounded-pill bg-alert px-1 text-center text-2xs font-bold leading-4 text-white">
                    {olvasatlan > 99 ? '99+' : olvasatlan}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      <nav ref={navRef} className="flex-1 overflow-y-auto px-2 pb-3">
        {favorites.length > 0 && (
          <Section
            title="Kedvencek"
            count={favorites.length}
            unread={sectionUnread(favorites)}
            collapsed={collapsed.favorites ?? false}
            onToggle={() => toggleSection('favorites')}
          >
            <DragList items={favorites} onReorder={(ids) => commitOrder('favorites', ids)}>
              {renderRow}
            </DragList>
          </Section>
        )}

        <Section
          title="Csatornák"
          count={rooms.length}
          unread={sectionUnread(rooms)}
          collapsed={collapsed.rooms ?? false}
          onToggle={() => toggleSection('rooms')}
          action={{ label: 'Új csatorna', onClick: () => setDialogOpen(true) }}
        >
          <DragList items={rooms} onReorder={(ids) => commitOrder('rooms', ids)}>
            {renderRow}
          </DragList>
          <button
            type="button"
            onClick={() => setBrowserOpen(true)}
            className="flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-sm text-ink-faint transition-colors duration-(--duration-quick) hover:bg-sidebar-hover hover:text-ink-muted"
          >
            <span aria-hidden="true" className="grid size-6 shrink-0 place-items-center">
              <Icon name="keres" />
            </span>
            Csatornák böngészése
          </button>
        </Section>

        <Section
          title="Közvetlen üzenetek"
          count={directs.length}
          unread={sectionUnread(directs)}
          collapsed={collapsed.dms ?? false}
          onToggle={() => toggleSection('dms')}
        >
          <DragList items={directs} onReorder={(ids) => commitOrder('directs', ids)}>
            {renderRow}
          </DragList>
          {directs.length === 0 && <Empty>Nyiss egyet a gyorsváltóval.</Empty>}
        </Section>
        {/*
          A SAJAT DOLGAIM (mentett, munkatarsak, naptar, piszkozat, jegyzet,
          emlekezteto) a lista ALJAN vannak: a csatornak es a beszelgetesek
          keruljenek elore. A fejlec nyil-gombja ide gorget.
        */}
        <div className="mt-2 border-t border-border-soft pt-3">
          <Link
            to="/saved"
            className="mb-2 flex items-center gap-2 rounded-control px-2 py-1.5 text-sm transition-colors duration-(--duration-quick) hover:bg-sidebar-hover [&.active]:bg-sidebar-active [&.active]:font-semibold [&.active]:shadow-(--shadow-soft)"
            activeProps={{ className: 'active' }}
          >
            <span
              aria-hidden="true"
              className="grid size-6 shrink-0 place-items-center text-ink-faint"
            >
              <Icon name="mentes" />
            </span>
            Mentett üzenetek
          </Link>

          {/* A kollegak nevjegyzeke: osztaly, mellek, telefon (AD2). */}
          <Link
            to="/people"
            className="mb-2 flex items-center gap-2 rounded-control px-2 py-1.5 text-sm transition-colors duration-(--duration-quick) hover:bg-sidebar-hover [&.active]:bg-sidebar-active [&.active]:font-semibold [&.active]:shadow-(--shadow-soft)"
            activeProps={{ className: 'active' }}
          >
            <span
              aria-hidden="true"
              className="grid size-6 shrink-0 place-items-center text-ink-faint"
            >
              <Icon name="tagok" />
            </span>
            Munkatársak
          </Link>

          {/* A sajat naptar: a csatornaim es a csoportjaim bejegyzesei egyutt (H1). */}
          <Link
            to="/naptar"
            className="mb-2 flex items-center gap-2 rounded-control px-2 py-1.5 text-sm transition-colors duration-(--duration-quick) hover:bg-sidebar-hover [&.active]:bg-sidebar-active [&.active]:font-semibold [&.active]:shadow-(--shadow-soft)"
            activeProps={{ className: 'active' }}
          >
            <span
              aria-hidden="true"
              className="grid size-6 shrink-0 place-items-center text-ink-faint"
            >
              <Icon name="naptar" />
            </span>
            Naptár
            <PendingInvitesBadge />
          </Link>

          {/* Piszkozatok, utemezett es elkuldott uzenetek egy helyen (U8). */}
          <Link
            to="/piszkozatok"
            className="mb-2 flex items-center gap-2 rounded-control px-2 py-1.5 text-sm transition-colors duration-(--duration-quick) hover:bg-sidebar-hover [&.active]:bg-sidebar-active [&.active]:font-semibold [&.active]:shadow-(--shadow-soft)"
            activeProps={{ className: 'active' }}
          >
            <span
              aria-hidden="true"
              className="grid size-6 shrink-0 place-items-center text-ink-faint"
            >
              <Icon name="szerkesztes" />
            </span>
            <span className="flex-1">Piszkozatok</span>
            {draftsByChannel.size > 0 && (
              <span className="text-2xs text-ink-faint">{draftsByChannel.size}</span>
            )}
          </Link>

          {/*
            A SAJAT JEGYZETEK a sajat csapatom helye.

            Egy masik ceg nezeteben ott allva ugy tunne, mintha oda tartozna -
            holott az en beszelgetesem, es a valto epp arrol szol, hogy egyszerre
            egy ceg latszik.
          */}
          {activeTeamId === me.teamId && (
            <button
              type="button"
              onClick={() => void openNotesChannel()}
              title="Csak te látod – ide írhatsz magadnak"
              className={[
                'mb-2 flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-sm',
                'transition-colors duration-(--duration-quick) hover:bg-sidebar-hover',
                notes && nyitottId === notes.id
                  ? 'bg-sidebar-active font-semibold shadow-(--shadow-soft)'
                  : '',
              ].join(' ')}
            >
              <span
                aria-hidden="true"
                className="grid size-6 shrink-0 place-items-center text-ink-faint"
              >
                <Icon name="jegyzet" />
              </span>
              Saját jegyzetek
            </button>
          )}

          {/*
            AZ EMLEKEZTETOK a sajat jegyzetek ala kerulnek.

            Ugyanaz a fajta hely: nem csatorna es nem beszelgetes, hanem a
            sajat dolgaim. A also sorban levo gomb UJAT allit be; ez a belepo
            azt nyitja meg, ami MAR SZOLT - a ket mozdulat annyira kulonbozik,
            hogy nem eri meg egyetlen gombra bizni.

            A jelveny csak akkor all ki, ha van mit kipipalni: egy allando
            nulla csak zaj lenne.
          */}
          <button
            type="button"
            onClick={() => {
              setReminderTab('korabbiak');
              setReminderOpen(true);
            }}
            title="Ami már szólt – itt tudod kipipálni"
            className="mb-2 flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-sm transition-colors duration-(--duration-quick) hover:bg-sidebar-hover"
          >
            <span
              aria-hidden="true"
              className="grid size-6 shrink-0 place-items-center text-ink-faint"
            >
              <Icon name="ora" />
            </span>
            <span className="flex-1">Emlékeztetők</span>
            {nyitottEmlekeztetok > 0 && (
              <span className="rounded-pill bg-alert px-1.5 py-px text-2xs font-semibold text-white">
                {nyitottEmlekeztetok}
              </span>
            )}
          </button>

          {/*
            Kattinthato: a lista megmutatja, mi megy el a nevedben, es ott le is
            lehet mondani - az ismetlodo uzenetet is (versenyaudit U9).
          */}
          {scheduled && scheduled.length > 0 && (
            <button
              type="button"
              onClick={() => setScheduledOpen(true)}
              className="mb-2 flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left text-xs text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
            >
              <span aria-hidden="true" className="grid size-6 shrink-0 place-items-center">
                <Icon name="ora" />
              </span>
              {scheduled.length} ütemezett üzenet
            </button>
          )}
          {scheduledOpen && <ScheduledDialog onClose={() => setScheduledOpen(false)} />}
        </div>
      </nav>

      {phoneRows.length > 0 && (
        <div className="border-t border-border-soft px-3 pt-2">
          {phoneRows.map(({ line, channel }) => (
            <ChannelRow
              key={line.id}
              channel={channel}
              label={phoneLineTitle(line, { prefix: false })}
              icon="telefon"
              hasDraft={draftsByChannel.has(channel.id)}
            />
          ))}
        </div>
      )}

      {/*
        A LABLEC ket sor: fent a profil, alatta a ritkabban hasznalt kapcsolok.

        A tema, a beallitasok es az adminisztracio korabban a fejlecben voltak,
        a nev mellett. Naponta egyszer sem nyulunk hozzajuk - a csatornalista
        viszont folyton kell -, tehat itt a helyuk, a profil mellett, amivel
        egy csoportot alkotnak: mind a SAJAT fiokunkrol szol.
      */}
      <footer className="border-t border-border-soft px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <div className="relative shrink-0">
            <Avatar userId={me.id} name={me.displayName} avatarUrl={me.avatarUrl} size="md" />
            <PresenceDot
              presence={status === 'online' ? 'active' : 'offline'}
              className="absolute -bottom-0.5 -right-0.5 ring-2 ring-sidebar"
            />
          </div>
          <button
            type="button"
            onClick={() => setProfileOpen(true)}
            className="min-w-0 flex-1 rounded-control px-1 py-0.5 text-left transition-colors duration-(--duration-quick) hover:bg-sidebar-hover"
            title="Profil és státusz"
          >
            <div className="truncate text-sm font-semibold">{nameOf(me)}</div>
            <div className="truncate text-xs text-ink-faint">
              <UserStatus user={me} fallback={me.title || 'Státusz beállítása'} />
            </div>
          </button>
        </div>

        <div className="mt-1.5 flex items-center gap-0.5">
          <RailButton
            label={
              preference === 'system'
                ? 'Téma: rendszer'
                : preference === 'light'
                  ? 'Téma: világos'
                  : 'Téma: sötét'
            }
            onClick={cycle}
          >
            <Icon
              name={
                preference === 'system'
                  ? 'temaRendszer'
                  : preference === 'light'
                    ? 'temaVilagos'
                    : 'temaSotet'
              }
            />
          </RailButton>

          <RailButton label="Beállítások" onClick={() => setNotifyOpen(true)}>
            <Icon name="beallitasok" />
          </RailButton>

          {/* Az admin belepo csak annak latszik, aki tenyleg az. */}
          {(me.role === 'admin' || me.role === 'owner') && (
            <Link
              to="/admin"
              title="Adminisztráció"
              aria-label="Adminisztráció"
              className="grid size-7 place-items-center rounded-control text-sm text-ink-muted transition-colors duration-(--duration-quick) hover:bg-sidebar-hover hover:text-ink"
            >
              <Icon name="admin" />
            </Link>
          )}

          {/*
            AUDIT - csak annak latszik, aki jogosult, vagy a szuperadminnak
            (o osztja a jogot es felugyeli a naplot).
          */}
          {(me.isSuperadmin || auditMe?.hasGrant) && (
            <Link
              to="/audit"
              title={
                auditMe?.pendingForMe
                  ? `Audit — ${auditMe.pendingForMe} kérés vár a döntésedre`
                  : 'Audit'
              }
              aria-label={
                auditMe?.pendingForMe
                  ? `Audit, ${auditMe.pendingForMe} kérés vár a döntésedre`
                  : 'Audit'
              }
              className="relative grid size-7 place-items-center rounded-control text-sm text-ink-muted transition-colors duration-(--duration-quick) hover:bg-sidebar-hover hover:text-ink"
            >
              <Icon name="lakat" />
              {/* Negy szem: a masik jogosultnak eszre kell vennie, hogy dontes kell. */}
              {(auditMe?.pendingForMe ?? 0) > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute -right-1 -top-1 min-w-4 rounded-pill bg-alert px-1 text-center text-[10px] font-bold leading-4 text-white"
                >
                  {auditMe!.pendingForMe}
                </span>
              )}
            </Link>
          )}

          {/*
            EMLEKEZTETO.

            Nem csatornahoz kotodik - barhol allsz, ugyanaz a dolga.
          */}
          <RailButton
            label="Emlékeztető"
            onClick={() => {
              setReminderTab('aktiv');
              setReminderOpen(true);
            }}
          >
            <Icon name="ora" />
          </RailButton>

          <RailButton
            label="Kijelentkezés"
            onClick={() => {
              clearDesktopSession();
              // Az eszkozon tartott offline adat es a kimeno sor sem maradhat itt (K2).
              forgetOfflineCache();
              clearOutbox();
              void logout()
                .catch(() => undefined)
                .then(() => {
                  window.location.href = '/login';
                });
            }}
          >
            <Icon name="kikapcsolas" />
          </RailButton>

          {/*
            VERZIOSZAM - kattintasra az Ujdonsagok (changelog, kereshetoen).
            A potty: ezen a gepen meg nem nezted meg a legutobbi kiadas ujdonsagait.
          */}
          <button
            type="button"
            onClick={() => {
              setSeenAtOpen(seenVersion);
              markVersionSeen();
              setNewsOpen(true);
            }}
            title={hasNews ? 'Újdonságok – van, amit még nem láttál' : 'Újdonságok'}
            aria-label={`Újdonságok, jelenlegi verzió: ${__APP_VERSION__}${hasNews ? ', új kiadás' : ''}`}
            className="relative ml-auto rounded-control px-1.5 py-0.5 font-mono text-[10px] text-ink-faint transition-colors duration-(--duration-quick) hover:bg-sidebar-hover hover:text-ink"
          >
            v{__APP_VERSION__}
            {hasNews && (
              <span
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-accent"
              />
            )}
          </button>
        </div>
      </footer>

      {dialogOpen && <NewChannelDialog onClose={() => setDialogOpen(false)} />}
      {browserOpen && <ChannelBrowser onClose={() => setBrowserOpen(false)} />}
      {profileOpen && <ProfileDialog me={me} onClose={() => setProfileOpen(false)} />}
      {reminderOpen && (
        <ReminderDialog kezdoFul={reminderTab} onClose={() => setReminderOpen(false)} />
      )}
      {notifyOpen && <SettingsDialog me={me} onClose={() => setNotifyOpen(false)} />}
      {newsOpen && (
        <Suspense fallback={null}>
          <ReleaseNotesDialog seenVersion={seenAtOpen} onClose={() => setNewsOpen(false)} />
        </Suspense>
      )}
    </aside>
  );
}

/**
 * OSSZECSUKOTT szekcio olvasatlanjai - ugyanazzal a szaballyal, mint a sorok:
 * a nemitott beszelgetes nem szamit bele (ott a sor sem jelez), az emlites
 * es a nem fogadott hivas viszont kulon latszik.
 */
interface SectionUnread {
  count: number;
  mentions: number;
  missedCall: boolean;
}

function sectionUnread(channels: ChannelWithMembership[]): SectionUnread {
  let count = 0;
  let mentions = 0;
  let missedCall = false;
  for (const channel of channels) {
    if (channel.hasMissedCall) missedCall = true;
    if (channel.membership.isMuted) continue;
    count += channel.unreadCount;
    mentions += channel.unreadMentions;
  }
  return { count, mentions, missedCall };
}

function Section({
  title,
  count,
  unread,
  collapsed,
  onToggle,
  action,
  children,
}: {
  title: string;
  count: number;
  unread: SectionUnread;
  collapsed: boolean;
  onToggle: () => void;
  action?: { label: string; onClick: () => void };
  children: React.ReactNode;
}) {
  return (
    <section className="mb-3">
      <div className="group/section flex items-center gap-1 px-1.5 pb-1 pt-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded text-2xs font-semibold uppercase tracking-[0.08em] text-ink-faint transition-colors duration-(--duration-quick) hover:text-ink-muted"
        >
          <span
            aria-hidden="true"
            className="inline-block transition-transform duration-(--duration-base) ease-(--ease-soft)"
            style={{ transform: collapsed ? 'rotate(-90deg)' : 'none' }}
          >
            <Icon name="le" />
          </span>
          <span className="truncate">{title}</span>
          {/*
            OSSZECSUKVA a fejlec mondja meg, van-e benne olvasatlan: kulonben
            egy becsukott szekcio uj uzenetei teljesen eltuntek a szem elol.
            Olvasatlan nelkul marad a csatornak szama.
          */}
          {collapsed && unread.count === 0 && count > 0 && (
            <span className="text-ink-faint/70">{count}</span>
          )}
        </button>

        {collapsed && unread.missedCall && (
          <span
            role="img"
            aria-label="nem fogadott hívás"
            title="Nem fogadott hívás"
            className="grid size-5 shrink-0 place-items-center rounded-full bg-alert text-2xs text-white"
          >
            <Icon name="kilepes" size="sm" />
          </span>
        )}
        {collapsed && unread.count > 0 && (
          <span
            title={
              unread.mentions > 0
                ? `${unread.count} olvasatlan üzenet · ${unread.mentions} említés`
                : `${unread.count} olvasatlan üzenet`
            }
            aria-label={`${unread.count} olvasatlan`}
            className={[
              'min-w-5 shrink-0 rounded-pill px-1.5 text-center text-2xs font-bold leading-5',
              unread.mentions > 0 ? 'bg-alert text-white' : 'bg-accent text-accent-ink',
            ].join(' ')}
          >
            {unread.count > 99 ? '99+' : unread.count}
          </span>
        )}

        {action && (
          <button
            type="button"
            onClick={action.onClick}
            title={action.label}
            aria-label={action.label}
            /*
              LATHATO gomb, nem raviteltre elobukkano.
              Korabban `opacity-0` volt: aki nem tudta, hogy ott van, az nem
              talalta meg - egy csatorna letrehozasa pedig nem rejtett muvelet.
            */
            className="grid size-6 shrink-0 place-items-center rounded-control text-ink-faint transition-colors duration-(--duration-quick) hover:bg-sidebar-hover hover:text-ink"
          >
            <Icon name="uj" size="sm" />
          </button>
        )}
      </div>

      {!collapsed && <div className="space-y-px">{children}</div>}
    </section>
  );
}

/**
 * Huzassal rendezheto lista.
 *
 * Konyvtar nelkul, a bongeszo sajat drag-and-dropjaval: ennyi kell hozza, es
 * egy uj fuggoseg csak sulyt adna a csomagnak.
 *
 * ─── Ket dontes, amit erdemes tudni ─────────────────────────────────────────
 *
 * 1. Csak a listan BELUL lehet mozgatni. Egy DM-et a csatornak koze huzni
 *    ugyis ertelmetlen lenne, egy nem-kedvencet a Kedvencek koze huzni pedig
 *    valojaban csillagozas - arra ott a csillag.
 *
 * 2. Alt+fel / Alt+le ugyanezt megteszi eger nelkul. A huzas onmagaban
 *    billentyuzettel elerhetetlen muvelet volna.
 */
function DragList({
  items,
  onReorder,
  children,
}: {
  items: ChannelWithMembership[];
  onReorder: (ids: string[]) => void;
  children: (channel: ChannelWithMembership) => React.ReactNode;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  /*
    A SORREND halasztva kovet, a TARTALOM azonnal.

    Egy uj uzenet a csatornat a lista tetejere viszi. A halasztott sorrend-
    valtas atmenetkent fut, igy a sor LATHATOAN atcsuszik a helyere, nem ugrik -
    latszik, mi mozdult. Csak a sorrend van halasztva: egy olvasatlan-szam vagy
    jelveny valtozasa azonnal, atmenet nelkul jelenik meg, kulonben minden
    beerkezo uzenet animaciot inditana.
  */
  const orderKey = items.map((item) => item.id).join(',');
  const shownOrder = useDeferredValue(orderKey);
  const shown = useMemo(() => {
    const rank = new Map(shownOrder.split(',').map((id, index) => [id, index]));
    // Ami a halasztott sorrendben meg nincs benne (uj csatorna), a vegere kerul.
    return [...items].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
  }, [items, shownOrder]);

  const clear = () => {
    setDragId(null);
    setOverId(null);
  };

  /** A `from` elemet a `to` helyere teszi, es kikuldi az uj sorrendet. */
  const move = (from: number, to: number) => {
    if (from === -1 || to < 0 || to >= shown.length || from === to) return;
    const ids = shown.map((item) => item.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved!);
    onReorder(ids);
  };

  return (
    <>
      {shown.map((item, index) => (
        <ViewTransition key={item.id} default="none" update="list-move">
          <div
            draggable
            onDragStart={(event) => {
              setDragId(item.id);
              event.dataTransfer.effectAllowed = 'move';
              // Adat nelkul a Firefox el sem inditja a huzast.
              event.dataTransfer.setData('text/plain', item.id);
            }}
            onDragEnd={clear}
            onDragOver={(event) => {
              if (!dragId) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setOverId(item.id);
            }}
            onDragLeave={() => setOverId((current) => (current === item.id ? null : current))}
            onDrop={(event) => {
              event.preventDefault();
              if (dragId && dragId !== item.id) {
                move(
                  shown.findIndex((other) => other.id === dragId),
                  index,
                );
              }
              clear();
            }}
            onKeyDown={(event) => {
              if (!event.altKey) return;
              const delta = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
              if (delta === 0) return;
              event.preventDefault();
              move(index, index + delta);
            }}
            className={[
              'relative rounded-control transition-opacity duration-(--duration-quick)',
              dragId === item.id ? 'opacity-40' : '',
            ].join(' ')}
          >
            {/* Hova esik: vekony vonal a celsor tetejen. */}
            {overId === item.id && dragId !== null && dragId !== item.id && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-1 -top-px h-0.5 rounded-pill bg-accent"
              />
            )}
            {children(item)}
          </div>
        </ViewTransition>
      ))}
    </>
  );
}

function ChannelRow({
  channel,
  label,
  userId,
  avatarUrl,
  presence,
  hasDraft,
  icon,
}: {
  channel: ChannelWithMembership;
  label: string;
  /** Sajat jel a lakat/kettoskereszt helyett (pl. a telefonos csatorna). */
  icon?: IconName;
  userId?: string | undefined;
  /** DM-nel a masik fel profilkepe - igy arcokat lat az ember, nem betuket. */
  avatarUrl?: string | null | undefined;
  presence?: string | undefined;
  hasDraft?: boolean;
}) {
  const params = useParams({ strict: false }) as { channelId?: string };
  const isActive = params.channelId === channel.id;
  const unread = channel.unreadCount;
  const mentions = channel.unreadMentions;
  const highlight = unread > 0 && !channel.membership.isMuted;

  return (
    <Link
      to="/c/$channelId"
      params={{ channelId: channel.id }}
      /*
        A bongeszo alapbol a HIVATKOZAST huzna (URL-t ejtve mashova). Itt a
        sor atrendezese a cel, ezert a huzas forrasa a burkolo elem.
      */
      draggable={false}
      className={[
        'flex items-center gap-2 rounded-control px-2 py-1.5 text-sm transition-[background-color,box-shadow,color,translate] duration-(--duration-quick)',
        /*
          OLVASATLAN SOR: ugyanaz a hatter, mint a kijelolt soré - halvanyabban.

          A felkover nev egymagaban elveszett a listaban: a szem a HATTERT
          veszi eszre eloszor, nem a betuvastagsagot.

          KIJELOLT SOR: akcentus-szinu keret + emelt arnyek + egy kis
          felemelkedes. A puszta hatter-kulonbseg keves volt - a felhasznalok
          osszekevertek az olvasatlan sorral, mert mindketto "feher kartya"
          volt. A keret szine a buboreke, igy a ket jel egy csaladba tartozik,
          de a forma (keret vs. hatter) mas.
        */
        isActive
          ? 'bg-sidebar-active font-semibold shadow-[0_3px_6px_oklch(30%_0.02_60/0.10),0_10px_20px_-4px_oklch(30%_0.02_60/0.18)] ring-2 ring-accent -translate-y-px'
          : highlight
            ? 'bg-sidebar-active/70 hover:bg-sidebar-active'
            : 'hover:bg-sidebar-hover',
        channel.membership.isMuted && !isActive ? 'text-ink-faint' : '',
      ].join(' ')}
    >
      {userId ? (
        <span className="relative">
          <Avatar userId={userId} name={label} avatarUrl={avatarUrl} size="sm" />
          <PresenceDot
            presence={presence}
            className={`absolute -bottom-0.5 -right-0.5 ring-2 ${isActive || highlight ? 'ring-sidebar-active' : 'ring-sidebar'}`}
          />
        </span>
      ) : (
        <span aria-hidden="true" className="grid size-6 shrink-0 place-items-center text-ink-faint">
          <Icon name={icon ?? (channel.kind === 'private' ? 'lakat' : 'csatorna')} size="sm" />
        </span>
      )}

      <span
        className={[
          'min-w-0 flex-1 truncate',
          highlight && !isActive ? 'font-semibold' : '',
          // Nem fogadott hivasnal a NEV is piros - a szem a szint eloszor veszi eszre.
          channel.hasMissedCall ? 'font-semibold text-alert' : '',
        ].join(' ')}
      >
        {label}
      </span>

      {hasDraft && (
        <span
          role="img"
          aria-label="piszkozat"
          title="Befejezetlen piszkozat"
          className="text-2xs text-accent"
        >
          <Icon name="szerkesztes" />
        </span>
      )}
      {/*
        NEM FOGADOTT HIVAS - kulon jelzes, nem a szamban.

        "Kerestek" es "irtak" ket kulonbozo dolog, es a hivas surgosebb: az
        olvasatlan-szamban elvegyulve pontosan ugy tunt volna el a szem elol,
        ahogy eddig. Nemitott beszelgetesben is latszik: a nemitas az
        uzenetekre szol, nem arra, hogy valaki fel akart hivni.

        Nincs kulon nyugtazas - amint elolvasod a beszelgetest, eltunik.
      */}
      {channel.hasMissedCall && (
        <span
          role="img"
          aria-label="nem fogadott hívás"
          title="Nem fogadott hívás"
          className="grid size-5 shrink-0 place-items-center rounded-full bg-alert text-2xs text-white"
        >
          <Icon name="kilepes" size="sm" />
        </span>
      )}
      {/* Folyik-e itt eppen hivas - a szerver adatbazisabol, nem becslesbol. */}
      <HuddleDot channelId={channel.id} />
      {channel.membership.isStarred && (
        <span role="img" aria-label="csillagozva" className="text-2xs text-ink-faint">
          <Icon name="csillag" />
        </span>
      )}
      {channel.membership.isMuted && (
        <span role="img" aria-label="némítva" className="text-2xs text-ink-faint">
          ⊘
        </span>
      )}
      {/*
        A SZAM MINDIG UGYANAZT JELENTI: hany olvasatlan uzenet van.

        A SZIN mondja meg, hogy surgos-e: piros, ha van kozte emlites (az
        rolam szol), egyebkent halvany. Korabban a piros buborekban az
        EMLITESEK szama allt - csakhogy igy egy hét olvasatlant tartalmazo
        csatorna "1"-et mutatott, es a szam egyik helyen sem volt
        osszeadhato a masikkal.

        Igy viszont az oldalsav szamai osszeadva pontosan azt adjak, ami a
        talcaikonon es a PWA-ikonon all.
      */}
      {highlight && (
        <span
          title={
            mentions > 0
              ? `${unread} olvasatlan üzenet · ${mentions} említés`
              : `${unread} olvasatlan üzenet`
          }
          aria-label={`${unread} olvasatlan`}
          className={[
            'min-w-5 rounded-pill px-1.5 text-center text-2xs leading-5',
            /*
              Az emlites piros marad - az rolad szol, annak kell a legerosebbnek
              lennie. A sima olvasatlan AKCENTUS-szinu: a halvany szurke
              buborek a sotet oldalsavon szinte nem latszott.
            */
            mentions > 0 ? 'bg-alert font-bold text-white' : 'bg-accent font-bold text-accent-ink',
          ].join(' ')}
        >
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
}

function RailButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="grid size-7 place-items-center rounded-control text-ink-faint transition-colors duration-(--duration-quick) hover:bg-sidebar-hover hover:text-ink"
    >
      {children}
    </button>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="px-2 py-2 text-xs text-ink-faint">{children}</p>;
}

function ConnectionPill({ status }: { status: string }) {
  const label =
    status === 'online'
      ? null
      : status === 'connecting'
        ? 'kapcsolódás…'
        : status === 'unauthorized'
          ? 'belépés kell'
          : 'nincs kapcsolat';

  // A status-burkolo online allapotban is a DOM-ban marad: a live regionak a szoveg ELOTT kell leteznie.
  return (
    <span role="status">
      {label && (
        <span className="rounded-pill bg-alert-soft px-2 py-0.5 text-2xs font-medium text-alert">
          {label}
        </span>
      )}
    </span>
  );
}

/**
 * A MEGVALASZOLATLAN MEGHIVOK szama a Naptar menupont mellett (docs/23) - ugyanaz
 * a jel, mint az olvasatlan uzeneteknel, hogy a szem ugyanugy megtalalja.
 */
function PendingInvitesBadge() {
  const { data: count } = usePendingInvites();
  if (!count) return null;
  return (
    <span
      title={`${count} megválaszolatlan meghívó`}
      aria-label={`${count} megválaszolatlan meghívó`}
      className="ml-auto min-w-5 rounded-pill bg-accent px-1.5 text-center text-2xs font-bold leading-5 text-accent-ink"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
